import Phaser from "phaser";

import type { DiamondSkinId } from "../domain/types";
import { KineticWorkshopScene, type WorkshopSceneCallbacks } from "./KineticWorkshopScene";
import { gameRenderScale } from "./renderScale";

export type WorkshopRuntime = {
  game: Phaser.Game;
  scene: KineticWorkshopScene;
  ready: Promise<void>;
  renderScale: number;
  resize(width: number, height: number): void;
};

function getRenderScale(): number {
  return gameRenderScale(window.devicePixelRatio || 1, document.documentElement.dataset.platform);
}

export function createWorkshopGame(
  parent: HTMLElement,
  callbacks: WorkshopSceneCallbacks,
  initialState: {
    tapReward: string;
    energyCurrent: number;
    energyCapacity: number;
    selectedSkinId: DiamondSkinId | null;
    musicEnabled: boolean;
  },
): WorkshopRuntime {
  const renderScale = getRenderScale();
  const logicalWidth = parent.clientWidth || 360;
  const logicalHeight = parent.clientHeight || 800;
  let resolveReady: () => void = () => undefined;
  const ready = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  const scene = new KineticWorkshopScene(
    { ...callbacks, onReady: resolveReady },
    initialState.tapReward,
    initialState.energyCurrent,
    initialState.energyCapacity,
    initialState.selectedSkinId,
    initialState.musicEnabled,
    renderScale,
  );
  const game = new Phaser.Game({
    type: Phaser.CANVAS,
    parent,
    width: Math.round(logicalWidth * renderScale),
    height: Math.round(logicalHeight * renderScale),
    transparent: true,
    antialias: true,
    render: {
      antialias: true,
      pixelArt: false,
      roundPixels: false,
    },
    scale: {
      mode: Phaser.Scale.NONE,
      autoCenter: Phaser.Scale.NO_CENTER,
    },
    input: {
      activePointers: 4,
      // Phaser otherwise also handles window mousedown on HTML controls.
      // On short screens the energy card overlaps the diamond hit area.
      windowEvents: false,
    },
    scene: [scene],
    banner: false,
  });

  return {
    game,
    scene,
    ready,
    renderScale,
    resize(width: number, height: number): void {
      game.scale.resize(Math.round(width * renderScale), Math.round(height * renderScale));
    },
  };
}
